import { useEffect, useState } from 'react';
import type { UnitSystem } from '@mlc/shared';

/**
 * Système d'unités d'affichage (US-34). Préférence personnelle, mémorisée en
 * `localStorage` comme le thème et la taille de texte du mode cuisine.
 *
 * **Affichage seulement.** Le §3.2 pose que les unités anglo-saxonnes sont
 * canoniques et que le parseur ne les convertit pas : « 1 cup » reste
 * `quantity=1, unit='cup'` en base. Ce réglage traduit à la lecture, et rien
 * n'est jamais réécrit — sans quoi une bascule aller-retour ferait dériver les
 * quantités par arrondis successifs.
 */
const STORAGE_KEY = 'mlc:systeme-unites';

export function readUnitSystem(): UnitSystem {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'imperial' ? 'imperial' : 'metrique';
  } catch {
    // Stockage refusé : le métrique est le défaut, et l'application marche.
    return 'metrique';
  }
}

export function useUnitSystem(): { system: UnitSystem; setSystem: (next: UnitSystem) => void } {
  const [system, setSystem] = useState<UnitSystem>(readUnitSystem);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, system);
    } catch {
      // Comme ci-dessus : un échec d'écriture n'empêche pas de lire dans le
      // système choisi pour cette session.
    }
  }, [system]);

  return { system, setSystem };
}
